import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import SearchableSelect, {
  type SearchableSelectOption,
} from '@/components/Approval/@components/SearchableSelect';
import type { ElawContract } from '@/types/approval';
import ContractOption from './ContractOption';
import { contractOptionLabel, matchesContractQuery } from './contractDisplay';
import { PickerRow } from './styles';

type ContractPickerProps = {
  contracts: ElawContract[];
  registeredIds: number[];
  value: string;
  onChange: (value: string) => void;
  onRegister: () => void;
  isLoading: boolean;
  disabled?: boolean;
};

/** 계약 선택 + 등록. 검색은 레거시 드롭다운과 같은 4필드(계약명·상대방·기안자·시작일) */
const ContractPicker = ({
  contracts,
  registeredIds,
  value,
  onChange,
  onRegister,
  isLoading,
  disabled,
}: ContractPickerProps) => {
  const { t } = useTranslation();

  const contractLabel = t('계약서');

  const options = useMemo<SearchableSelectOption[]>(
    () =>
      contracts.map((contract) => ({
        label: contractOptionLabel(
          contract,
          `${contractLabel} ${contract.contractId}`,
        ),
        labelNode: (
          <ContractOption
            contract={contract}
            fallbackName={`${contractLabel} ${contract.contractId}`}
          />
        ),
        value: String(contract.contractId),
        disabled: registeredIds.includes(contract.contractId),
      })),
    [contracts, registeredIds, contractLabel],
  );

  const contractsById = useMemo(
    () => new Map(contracts.map((c) => [String(c.contractId), c])),
    [contracts],
  );

  const filter = useCallback(
    (option: SearchableSelectOption, query: string) => {
      const contract = contractsById.get(option.value);
      return contract ? matchesContractQuery(contract, query) : false;
    },
    [contractsById],
  );

  const placeholder = isLoading
    ? t('계약서 목록을 불러오는 중입니다.')
    : t('추가할 계약서를 선택해 주세요.');

  return (
    <PickerRow>
      <SearchableSelect
        className="contract-picker-select"
        options={options}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled || isLoading}
        searchPlaceholder={t('계약명·상대방·기안자·시작일 검색')}
        noOptionsText={t('등록 가능한 계약서가 없습니다.')}
        filter={filter}
        renderValue={(selected) =>
          options.find((option) => option.value === selected)?.label ?? ''
        }
      />
      <BoxButton
        variant="secondary"
        size="medium"
        onClick={onRegister}
        disabled={disabled || isLoading}
      >
        {t('등록')}
      </BoxButton>
    </PickerRow>
  );
};

export default ContractPicker;
